import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Home, ArrowLeft, Plus, Trash2, Edit2, Save, X,
  Wallet, TrendingDown, TrendingUp, Calendar, Euro,
  ChevronLeft, ChevronRight, PieChart, Filter,
  Banknote, ShoppingCart, Coffee, Car, Phone, Zap,
  Upload, Camera, FileText, Loader2, Sparkles, Eye, Image,
  FileDown, Check, Square, CheckSquare
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export default function CashFlowPage() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  
  // États
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [cashPayments, setCashPayments] = useState([]); // Paiements fournisseurs en espèces
  const [cashExpenses, setCashExpenses] = useState([]); // Dépenses diverses en espèces
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [viewMode, setViewMode] = useState('month'); // month, year
  
  // États pour l'analyse IA
  const [analyzing, setAnalyzing] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [aiResult, setAiResult] = useState(null);
  
  // Modal de visualisation de fichier
  const [showFileModal, setShowFileModal] = useState(false);
  const [viewingFile, setViewingFile] = useState(null);
  
  // Import depuis comptabilité
  const [showImportModal, setShowImportModal] = useState(false);
  const [accountingDocs, setAccountingDocs] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [selectedDocs, setSelectedDocs] = useState([]);
  // Permettre d'éditer le montant inline avant import (pour les photos non lues par l'IA)
  const [editedAmounts, setEditedAmounts] = useState({}); // { [docId]: '12.50' }
  
  // Formulaire
  const [formData, setFormData] = useState({
    description: '',
    amount: '',
    date: new Date().toISOString().slice(0, 10),
    category: 'fournisseur',
    supplier: '',
    reference: '',
    notes: '',
    file_data: null,
    file_name: null,
    file_type: null
  });

  // Catégories de dépenses espèces
  const expenseCategories = [
    { id: 'fournisseur', name: 'Paiement Fournisseur', icon: ShoppingCart, color: '#3B82F6' },
    { id: 'fournitures', name: 'Fournitures', icon: ShoppingCart, color: '#10B981' },
    { id: 'transport', name: 'Transport/Carburant', icon: Car, color: '#F59E0B' },
    { id: 'repas', name: 'Repas/Restaurant', icon: Coffee, color: '#8B5CF6' },
    { id: 'communication', name: 'Communication', icon: Phone, color: '#EC4899' },
    { id: 'services', name: 'Services/Prestataires', icon: Zap, color: '#EF4444' },
    { id: 'general', name: 'Autre dépense', icon: Banknote, color: '#6B7280' }
  ];

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    loadData();
  }, [token, selectedMonth, viewMode]);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = viewMode === 'year' 
        ? { year: selectedMonth.slice(0, 4) }
        : { month: selectedMonth };
      
      const [paymentsRes, expensesRes] = await Promise.all([
        axios.get(`${API}/api/cash-flow/payments`, {
          params,
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/cash-flow/expenses`, {
          params,
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setCashPayments(paymentsRes.data);
      setCashExpenses(expensesRes.data);
    } catch (error) {
      console.error('Erreur chargement:', error);
      // Si l'API n'existe pas encore, initialiser avec des tableaux vides
      setCashPayments([]);
      setCashExpenses([]);
    } finally {
      setLoading(false);
    }
  };

  const navigatePeriod = (direction) => {
    const date = new Date(selectedMonth + '-01');
    if (viewMode === 'year') {
      date.setFullYear(date.getFullYear() + direction);
    } else {
      date.setMonth(date.getMonth() + direction);
    }
    setSelectedMonth(date.toISOString().slice(0, 7));
  };

  const formatMonth = (monthStr) => {
    const date = new Date(monthStr + '-01');
    return date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  // Charger les documents comptables pour l'import
  const loadAccountingDocs = async () => {
    setLoadingDocs(true);
    try {
      const res = await axios.get(`${API}/api/accounting/documents/all`, {
        params: { limit: 500 },
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filtrer pour ne garder que les documents NON encore importés
      // Inclure TOUS les documents avec fichier (photos) pour permettre l'import
      const docs = (res.data.documents || []).filter(doc => 
        !doc.imported_to_cashflow && (  // Exclure les déjà importés
          doc.payment_method === 'cash' || 
          doc.payment_method === 'especes' || 
          doc.type === 'facture_achat' ||
          doc.has_file === true  // Inclure TOUS les documents avec fichier/photo
        )
      );
      setAccountingDocs(docs);
    } catch (error) {
      console.error('Erreur chargement documents:', error);
      toast.error('Erreur lors du chargement des documents');
    } finally {
      setLoadingDocs(false);
    }
  };

  // Ouvrir le modal d'import
  const openImportModal = () => {
    setSelectedDocs([]);
    setEditedAmounts({});
    loadAccountingDocs();
    setShowImportModal(true);
  };

  // Basculer la sélection d'un document
  const toggleDocSelection = (docId) => {
    setSelectedDocs(prev => 
      prev.includes(docId) 
        ? prev.filter(id => id !== docId)
        : [...prev, docId]
    );
  };

  // Sélectionner/désélectionner tout
  const toggleSelectAll = () => {
    if (selectedDocs.length === accountingDocs.length) {
      setSelectedDocs([]);
    } else {
      setSelectedDocs(accountingDocs.map(d => d.id));
    }
  };

  // Importer les documents sélectionnés comme paiements espèces
  const importSelectedDocs = async () => {
    if (selectedDocs.length === 0) {
      toast.error('Sélectionnez au moins un document');
      return;
    }

    // Vérifier que tous les documents sélectionnés ont un montant > 0
    const docsToImport = accountingDocs.filter(d => selectedDocs.includes(d.id));
    const missingAmounts = docsToImport.filter(doc => {
      const editedVal = editedAmounts[doc.id];
      const finalAmount = editedVal !== undefined && editedVal !== ''
        ? parseFloat(editedVal)
        : (doc.amount_ttc || doc.amount || 0);
      return !finalAmount || isNaN(finalAmount) || finalAmount <= 0;
    });

    if (missingAmounts.length > 0) {
      toast.error(
        `${missingAmounts.length} document(s) sans montant valide. Saisissez le montant manuellement (champ ✏️) ou désélectionnez-les.`,
        { duration: 5000 }
      );
      return;
    }

    try {
      let imported = 0;
      let skipped = 0;
      
      for (const doc of docsToImport) {
        try {
          // Utiliser le montant édité si disponible, sinon le montant du document
          const editedVal = editedAmounts[doc.id];
          const finalAmount = editedVal !== undefined && editedVal !== ''
            ? parseFloat(editedVal)
            : (doc.amount_ttc || doc.amount || 0);

          // Créer un paiement espèces à partir du document comptable
          await axios.post(`${API}/api/cash-flow/payments`, {
            description: doc.title || doc.reference || doc.supplier || doc.description || 'Import comptabilité',
            amount: finalAmount,
            date: doc.date || new Date().toISOString().slice(0, 10),
            category: 'fournisseur',
            supplier: doc.supplier || '',
            reference: doc.reference || doc.number || '',
            notes: `Importé depuis comptabilité${doc.has_file ? ' (avec justificatif)' : ''}`,
            source: 'accounting_import',
            accounting_doc_id: doc.id
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          imported++;
        } catch (err) {
          // Document déjà importé ou erreur
          console.warn(`Document ${doc.id} non importé:`, err.response?.data?.detail);
          skipped++;
        }
      }

      if (imported > 0) {
        toast.success(`${imported} document(s) importé(s) avec succès${skipped > 0 ? ` (${skipped} ignoré(s))` : ''}`);
        setShowImportModal(false);
        setSelectedDocs([]);
        setEditedAmounts({});
        loadData(); // Recharger les données pour voir les nouveaux paiements
      } else {
        toast.warning('Aucun document importé (déjà importés ou erreur)');
      }
    } catch (error) {
      console.error('Erreur import:', error);
      toast.error('Erreur lors de l\'import');
    }
  };


  const openPaymentModal = (item = null) => {
    resetFileState();
    if (item) {
      setEditingItem(item);
      setFormData({
        description: item.description,
        amount: item.amount.toString(),
        date: item.date,
        category: item.category || 'fournisseur',
        supplier: item.supplier || '',
        reference: item.reference || '',
        notes: item.notes || '',
        file_data: null,
        file_name: null,
        file_type: null
      });
    } else {
      setEditingItem(null);
      setFormData({
        description: '',
        amount: '',
        date: new Date().toISOString().slice(0, 10),
        category: 'fournisseur',
        supplier: '',
        reference: '',
        notes: '',
        file_data: null,
        file_name: null,
        file_type: null
      });
    }
    setShowPaymentModal(true);
  };

  const openExpenseModal = (item = null) => {
    resetFileState();
    if (item) {
      setEditingItem(item);
      setFormData({
        description: item.description,
        amount: item.amount.toString(),
        date: item.date,
        category: item.category || 'general',
        supplier: item.supplier || '',
        reference: item.reference || '',
        notes: item.notes || '',
        file_data: null,
        file_name: null,
        file_type: null
      });
    } else {
      setEditingItem(null);
      setFormData({
        description: '',
        amount: '',
        date: new Date().toISOString().slice(0, 10),
        category: 'general',
        supplier: '',
        reference: '',
        notes: '',
        file_data: null,
        file_name: null,
        file_type: null
      });
    }
    setShowExpenseModal(true);
  };

  const savePayment = async () => {
    if (!formData.supplier || !formData.amount) {
      toast.error('Veuillez remplir le fournisseur et le montant');
      return;
    }

    try {
      const payload = {
        description: formData.description || `Paiement - ${formData.supplier}`,
        amount: parseFloat(formData.amount),
        date: formData.date,
        category: formData.category,
        supplier: formData.supplier,
        reference: formData.reference,
        notes: formData.notes,
        type: 'payment',
        // Données du justificatif
        file_data: formData.file_data,
        file_name: formData.file_name,
        file_type: formData.file_type,
        has_file: !!formData.file_data
      };

      if (editingItem) {
        await axios.put(`${API}/api/cash-flow/payments/${editingItem.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Paiement fournisseur mis à jour');
      } else {
        await axios.post(`${API}/api/cash-flow/payments`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // Vérifier si la date du paiement est dans un autre mois que l'affichage courant
        const paymentMonth = (formData.date || '').slice(0, 7);
        if (paymentMonth && paymentMonth !== selectedMonth) {
          const monthLabel = formatMonth(paymentMonth);
          toast.success(`Paiement enregistré dans ${monthLabel}`, {
            description: 'Cliquez sur "Y aller" pour voir ce mois.',
            duration: 8000,
            action: {
              label: 'Y aller',
              onClick: () => setSelectedMonth(paymentMonth)
            }
          });
        } else {
          toast.success('Paiement fournisseur enregistré');
        }
      }
      
      setShowPaymentModal(false);
      resetFileState();
      loadData();
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const saveExpense = async () => {
    if (!formData.description || !formData.amount) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      const payload = {
        description: formData.description,
        amount: parseFloat(formData.amount),
        date: formData.date,
        category: formData.category,
        supplier: formData.supplier,
        reference: formData.reference,
        notes: formData.notes,
        type: 'expense',
        // Données du justificatif
        file_data: formData.file_data,
        file_name: formData.file_name,
        file_type: formData.file_type,
        has_file: !!formData.file_data
      };

      if (editingItem) {
        await axios.put(`${API}/api/cash-flow/expenses/${editingItem.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Dépense mise à jour');
      } else {
        await axios.post(`${API}/api/cash-flow/expenses`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // Vérifier si la date de la dépense est dans un autre mois que l'affichage courant
        const expenseMonth = (formData.date || '').slice(0, 7);
        if (expenseMonth && expenseMonth !== selectedMonth) {
          const monthLabel = formatMonth(expenseMonth);
          toast.success(`Dépense enregistrée dans ${monthLabel}`, {
            description: 'Cliquez sur "Y aller" pour voir ce mois.',
            duration: 8000,
            action: {
              label: 'Y aller',
              onClick: () => setSelectedMonth(expenseMonth)
            }
          });
        } else {
          toast.success('Dépense enregistrée');
        }
      }
      
      setShowExpenseModal(false);
      resetFileState();
      loadData();
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const deletePayment = async (id) => {
    if (!window.confirm('Supprimer ce paiement ?')) return;
    try {
      await axios.delete(`${API}/api/cash-flow/payments/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Paiement supprimé');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const deleteExpense = async (id) => {
    if (!window.confirm('Supprimer cette dépense ?')) return;
    try {
      await axios.delete(`${API}/api/cash-flow/expenses/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Dépense supprimée');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // ========== ANALYSE IA DES JUSTIFICATIFS ==========
  
  const handleFileSelect = async (e, modalType) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Vérifier la taille
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Fichier trop volumineux (max 10 Mo)');
      return;
    }
    
    setSelectedFile(file);
    
    // Créer preview
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setFilePreview(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setFilePreview(null);
    }
    
    // Analyser avec IA
    await analyzeWithAI(file, modalType);
  };
  
  const analyzeWithAI = async (file, modalType) => {
    setAnalyzing(true);
    setAiResult(null);
    
    try {
      // Convertir en base64
      const fileData = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      
      // Appeler l'API d'analyse
      const response = await axios.post(`${API}/api/ai-document/analyze`, {
        file_data: fileData,
        file_type: file.type,
        file_name: file.name
      }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 60000
      });
      
      if (response.data.success) {
        const extracted = response.data.extracted_data;
        setAiResult(extracted);
        
        // Pré-remplir le formulaire avec les données extraites
        setFormData(prev => ({
          ...prev,
          description: extracted.fournisseur 
            ? `${extracted.type_document || 'Paiement'} - ${extracted.fournisseur}`
            : prev.description,
          amount: extracted.montant ? Math.abs(extracted.montant).toString() : prev.amount,
          date: extracted.date || prev.date,
          supplier: extracted.fournisseur || prev.supplier,
          reference: extracted.reference || prev.reference,
          notes: extracted.description || prev.notes,
          category: detectCategory(extracted),
          file_data: fileData,
          file_name: file.name,
          file_type: file.type
        }));
        
        toast.success('Justificatif analysé avec succès !');
      } else {
        toast.error('Impossible d\'analyser ce justificatif');
      }
    } catch (error) {
      console.error('Erreur analyse IA:', error);
      toast.error('Erreur lors de l\'analyse');
      
      // Stocker quand même le fichier
      const fileData = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result.split(',')[1]);
        reader.readAsDataURL(file);
      });
      
      setFormData(prev => ({
        ...prev,
        file_data: fileData,
        file_name: file.name,
        file_type: file.type
      }));
    } finally {
      setAnalyzing(false);
    }
  };
  
  const detectCategory = (extracted) => {
    const text = `${extracted.fournisseur || ''} ${extracted.description || ''} ${extracted.type_document || ''}`.toLowerCase();
    
    if (text.includes('carburant') || text.includes('essence') || text.includes('gasoil') || text.includes('total') || text.includes('station')) {
      return 'transport';
    }
    if (text.includes('restaurant') || text.includes('repas') || text.includes('café') || text.includes('boulangerie')) {
      return 'repas';
    }
    if (text.includes('orange') || text.includes('sfr') || text.includes('bouygues') || text.includes('free') || text.includes('téléphone')) {
      return 'communication';
    }
    if (text.includes('facture') || text.includes('fournisseur') || text.includes('marchandise')) {
      return 'fournisseur';
    }
    return 'general';
  };
  
  const resetFileState = () => {
    setSelectedFile(null);
    setFilePreview(null);
    setAiResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  // Visualiser un fichier justificatif
  const viewFile = async (itemId) => {
    try {
      const response = await axios.get(`${API}/api/cash-flow/file/${itemId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.file_data) {
        setViewingFile({
          data: response.data.file_data,
          name: response.data.file_name,
          type: response.data.file_type
        });
        setShowFileModal(true);
      }
    } catch (error) {
      console.error('Erreur chargement fichier:', error);
      toast.error('Impossible de charger le fichier');
    }
  };

  // Calculs - Focus sur les SORTIES d'espèces (paiements fournisseurs + dépenses)
  const totalPayments = cashPayments.reduce((sum, p) => sum + p.amount, 0);
  const totalExpenses = cashExpenses.reduce((sum, e) => sum + e.amount, 0);
  const totalSorties = totalPayments + totalExpenses; // Total des sorties espèces

  const getCategoryInfo = (categoryId) => {
    return expenseCategories.find(c => c.id === categoryId) || expenseCategories[6];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button onClick={() => {
              sessionStorage.setItem('reports_previous_path', '/cash-flow');
              navigate('/reports');
            }} variant="ghost" size="sm" className="text-white/70">
              <ArrowLeft className="w-4 h-4 mr-1" /> Rapports
            </Button>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Wallet className="w-6 h-6 text-emerald-400" />
              Flux Espèces
            </h1>
          </div>
          <Button 
            onClick={() => {
              sessionStorage.removeItem('reports_authenticated');
              navigate('/');
            }} 
            variant="ghost" 
            size="sm" 
            className="text-amber-400 hover:text-amber-300 hover:bg-amber-400/10"
          >
            <Home className="w-4 h-4 mr-1" />
            Quitter Rapports
          </Button>
        </div>

        {/* Navigation Période */}
        <div className="flex items-center justify-between mb-6 bg-white/5 rounded-xl p-4 border border-white/10">
          <div className="flex items-center gap-2">
            <Button onClick={() => navigatePeriod(-1)} variant="ghost" size="sm" className="text-white/70">
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <span className="text-lg font-semibold text-white min-w-[200px] text-center">
              {viewMode === 'year' ? selectedMonth.slice(0, 4) : formatMonth(selectedMonth)}
            </span>
            <Button onClick={() => navigatePeriod(1)} variant="ghost" size="sm" className="text-white/70">
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
          
          <div className="flex items-center gap-2">
            <Button 
              onClick={() => setViewMode('month')} 
              variant={viewMode === 'month' ? 'default' : 'ghost'}
              size="sm"
              className={viewMode === 'month' ? 'bg-emerald-500' : 'text-white/70'}
            >
              Mois
            </Button>
            <Button 
              onClick={() => setViewMode('year')} 
              variant={viewMode === 'year' ? 'default' : 'ghost'}
              size="sm"
              className={viewMode === 'year' ? 'bg-emerald-500' : 'text-white/70'}
            >
              Année
            </Button>
          </div>
        </div>

        {/* Résumé - Focus sur les SORTIES ESPÈCES */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 border border-blue-500/30 rounded-xl p-4">
            <div className="flex items-center gap-3 mb-2">
              <ShoppingCart className="w-5 h-5 text-blue-400" />
              <span className="text-white/70 text-sm">Paiements Fournisseurs</span>
            </div>
            <div className="text-2xl font-bold text-blue-400">{formatCurrency(totalPayments)}</div>
            <div className="text-sm text-white/50 mt-1">{cashPayments.length} paiement(s)</div>
          </div>
          
          <div className="bg-gradient-to-br from-amber-500/20 to-amber-600/10 border border-amber-500/30 rounded-xl p-4">
            <div className="flex items-center gap-3 mb-2">
              <TrendingDown className="w-5 h-5 text-amber-400" />
              <span className="text-white/70 text-sm">Dépenses Diverses</span>
            </div>
            <div className="text-2xl font-bold text-amber-400">{formatCurrency(totalExpenses)}</div>
            <div className="text-sm text-white/50 mt-1">{cashExpenses.length} dépense(s)</div>
          </div>
          
          <div className="bg-gradient-to-br from-red-500/20 to-red-600/10 border border-red-500/30 rounded-xl p-4">
            <div className="flex items-center gap-3 mb-2">
              <Wallet className="w-5 h-5 text-red-400" />
              <span className="text-white/70 text-sm">Total Sorties Espèces</span>
            </div>
            <div className="text-2xl font-bold text-red-400">{formatCurrency(totalSorties)}</div>
            <div className="text-sm text-white/50 mt-1">
              {cashPayments.length + cashExpenses.length} opération(s)
            </div>
          </div>
        </div>

        {/* Deux colonnes */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Paiements Fournisseurs en espèces */}
          <div className="bg-white/5 backdrop-blur border border-white/10 rounded-xl overflow-hidden">
            <div className="p-4 border-b border-white/10 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <ShoppingCart className="w-5 h-5 text-blue-400" />
                Paiements Fournisseurs
              </h2>
              <div className="flex gap-2">
                <Button onClick={openImportModal} size="sm" variant="outline" className="border-blue-400 text-blue-400 hover:bg-blue-500/20" data-testid="cashflow-import-btn">
                  <FileDown className="w-4 h-4 mr-1" /> Importer
                </Button>
                <Button onClick={() => openPaymentModal()} size="sm" className="bg-blue-500 hover:bg-blue-600">
                  <Plus className="w-4 h-4 mr-1" /> Ajouter
                </Button>
              </div>
            </div>
            
            {cashPayments.length === 0 ? (
              <div className="p-8 text-center text-white/50">
                <ShoppingCart className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>Aucun paiement fournisseur ce {viewMode === 'year' ? 'cette année' : 'mois'}</p>
                <p className="text-xs mt-2">Scannez un justificatif pour ajouter automatiquement</p>
              </div>
            ) : (
              <div className="divide-y divide-white/10 max-h-[400px] overflow-y-auto">
                {cashPayments.map(payment => (
                  <div key={payment.id} className="p-4 flex items-center gap-4 hover:bg-white/5">
                    <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center relative">
                      <Banknote className="w-5 h-5 text-blue-400" />
                      {payment.has_file && (
                        <div className="absolute -top-1 -right-1 w-4 h-4 bg-emerald-500 rounded-full flex items-center justify-center">
                          <FileText className="w-2.5 h-2.5 text-white" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-white">{payment.description}</div>
                      <div className="text-sm text-white/60 flex items-center gap-2">
                        <span>{payment.date}</span>
                        {payment.supplier && <><span>•</span><span>{payment.supplier}</span></>}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-blue-400">{formatCurrency(payment.amount)}</div>
                    </div>
                    <div className="flex items-center gap-1">
                      {payment.has_file && (
                        <Button size="sm" variant="ghost" className="text-emerald-400" onClick={() => viewFile(payment.id)}>
                          <Eye className="w-4 h-4" />
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" className="text-white/70" onClick={() => openPaymentModal(payment)}>
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-400" onClick={() => deletePayment(payment.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Dépenses en espèces */}
          <div className="bg-white/5 backdrop-blur border border-white/10 rounded-xl overflow-hidden">
            <div className="p-4 border-b border-white/10 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <TrendingDown className="w-5 h-5 text-red-400" />
                Dépenses Espèces
              </h2>
              <Button onClick={() => openExpenseModal()} size="sm" className="bg-red-500 hover:bg-red-600">
                <Plus className="w-4 h-4 mr-1" /> Ajouter
              </Button>
            </div>
            
            {cashExpenses.length === 0 ? (
              <div className="p-8 text-center text-white/50">
                <ShoppingCart className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>Aucune dépense espèces ce {viewMode === 'year' ? 'cette année' : 'mois'}</p>
              </div>
            ) : (
              <div className="divide-y divide-white/10 max-h-[400px] overflow-y-auto">
                {cashExpenses.map(expense => {
                  const catInfo = getCategoryInfo(expense.category);
                  const IconComp = catInfo.icon;
                  return (
                    <div key={expense.id} className="p-4 flex items-center gap-4 hover:bg-white/5">
                      <div 
                        className="w-10 h-10 rounded-lg flex items-center justify-center relative"
                        style={{ backgroundColor: catInfo.color + '30' }}
                      >
                        <IconComp className="w-5 h-5" style={{ color: catInfo.color }} />
                        {expense.has_file && (
                          <div className="absolute -top-1 -right-1 w-4 h-4 bg-emerald-500 rounded-full flex items-center justify-center">
                            <FileText className="w-2.5 h-2.5 text-white" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-white">{expense.description}</div>
                        <div className="text-sm text-white/60 flex items-center gap-2">
                          <span>{expense.date}</span>
                          <span>•</span>
                          <span style={{ color: catInfo.color }}>{catInfo.name}</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold text-red-400">-{formatCurrency(expense.amount)}</div>
                      </div>
                      <div className="flex items-center gap-1">
                        {expense.has_file && (
                          <Button size="sm" variant="ghost" className="text-emerald-400" onClick={() => viewFile(expense.id)}>
                            <Eye className="w-4 h-4" />
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" className="text-white/70" onClick={() => openExpenseModal(expense)}>
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-400" onClick={() => deleteExpense(expense.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Modal Paiement */}
        {/* Modal Paiement Fournisseur en Espèces */}
        {showPaymentModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-xl border border-gray-700 max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-white flex items-center gap-2">
                    <ShoppingCart className="w-5 h-5 text-blue-400" />
                    {editingItem ? 'Modifier' : 'Nouveau'} Paiement Fournisseur
                  </h2>
                  <Button onClick={() => { setShowPaymentModal(false); resetFileState(); }} variant="ghost" size="sm" className="text-gray-400">
                    <X className="w-5 h-5" />
                  </Button>
                </div>

                {/* Zone d'import justificatif */}
                <div className="mb-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="w-5 h-5 text-blue-400" />
                    <span className="text-blue-300 font-medium">Scanner le justificatif (Analyse IA)</span>
                  </div>
                  
                  {analyzing ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="w-6 h-6 text-blue-400 animate-spin mr-2" />
                      <span className="text-white/60">Analyse en cours...</span>
                    </div>
                  ) : selectedFile ? (
                    <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                      {filePreview ? (
                        <img src={filePreview} alt="Preview" className="w-16 h-16 object-cover rounded" />
                      ) : (
                        <FileText className="w-12 h-12 text-white/40" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm truncate">{selectedFile.name}</p>
                        <p className="text-white/50 text-xs">{(selectedFile.size / 1024).toFixed(0)} KB</p>
                        {aiResult && (
                          <p className="text-emerald-400 text-xs mt-1">
                            ✓ {aiResult.fournisseur || 'Document'} - {aiResult.montant}€
                          </p>
                        )}
                      </div>
                      <Button onClick={resetFileState} variant="ghost" size="sm" className="text-red-400">
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => handleFileSelect(e, 'payment')}
                        className="hidden"
                      />
                      <input
                        ref={cameraInputRef}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        onChange={(e) => handleFileSelect(e, 'payment')}
                        className="hidden"
                      />
                      <Button 
                        onClick={() => fileInputRef.current?.click()} 
                        variant="outline" 
                        className="flex-1 border-blue-500/50 text-blue-300"
                      >
                        <Upload className="w-4 h-4 mr-2" /> Importer
                      </Button>
                      <Button 
                        onClick={() => cameraInputRef.current?.click()} 
                        variant="outline" 
                        className="flex-1 border-blue-500/50 text-blue-300"
                      >
                        <Camera className="w-4 h-4 mr-2" /> Photo
                      </Button>
                    </div>
                  )}
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="text-sm text-white/70 mb-1 block">Fournisseur *</label>
                    <Input
                      value={formData.supplier}
                      onChange={(e) => setFormData({...formData, supplier: e.target.value, description: `Paiement - ${e.target.value}`})}
                      placeholder="Ex: Mobilax, Grossiste..."
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-white/70 mb-1 block">Montant (€) *</label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.amount}
                        onChange={(e) => setFormData({...formData, amount: e.target.value})}
                        placeholder="0.00"
                        className="bg-white/10 border-white/20 text-white text-lg font-semibold"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-white/70 mb-1 block">Date *</label>
                      <Input
                        type="date"
                        value={formData.date}
                        onChange={(e) => setFormData({...formData, date: e.target.value})}
                        className="bg-white/10 border-white/20 text-white"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-sm text-white/70 mb-1 block">Référence / N° Facture</label>
                    <Input
                      value={formData.reference}
                      onChange={(e) => setFormData({...formData, reference: e.target.value})}
                      placeholder="Ex: FAC-2026-001"
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>

                  <div>
                    <label className="text-sm text-white/70 mb-1 block">Notes</label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({...formData, notes: e.target.value})}
                      placeholder="Détails de l'achat..."
                      rows={2}
                      className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white resize-none"
                    />
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <Button onClick={() => { setShowPaymentModal(false); resetFileState(); }} variant="outline" className="flex-1 border-white/20 text-white">
                    Annuler
                  </Button>
                  <Button onClick={savePayment} className="flex-1 bg-blue-500 hover:bg-blue-600">
                    <Save className="w-4 h-4 mr-2" />
                    {editingItem ? 'Modifier' : 'Enregistrer'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal Dépense Espèces */}
        {showExpenseModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-xl border border-gray-700 max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-white flex items-center gap-2">
                    <TrendingDown className="w-5 h-5 text-red-400" />
                    {editingItem ? 'Modifier' : 'Nouvelle'} Dépense Espèces
                  </h2>
                  <Button onClick={() => { setShowExpenseModal(false); resetFileState(); }} variant="ghost" size="sm" className="text-gray-400">
                    <X className="w-5 h-5" />
                  </Button>
                </div>

                {/* Zone d'import justificatif */}
                <div className="mb-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl">
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="w-5 h-5 text-amber-400" />
                    <span className="text-amber-300 font-medium">Scanner le ticket (Analyse IA)</span>
                  </div>
                  
                  {analyzing ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="w-6 h-6 text-amber-400 animate-spin mr-2" />
                      <span className="text-white/60">Analyse en cours...</span>
                    </div>
                  ) : selectedFile ? (
                    <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                      {filePreview ? (
                        <img src={filePreview} alt="Preview" className="w-16 h-16 object-cover rounded" />
                      ) : (
                        <FileText className="w-12 h-12 text-white/40" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm truncate">{selectedFile.name}</p>
                        <p className="text-white/50 text-xs">{(selectedFile.size / 1024).toFixed(0)} KB</p>
                        {aiResult && (
                          <p className="text-emerald-400 text-xs mt-1">
                            ✓ {aiResult.fournisseur || 'Ticket'} - {aiResult.montant}€
                          </p>
                        )}
                      </div>
                      <Button onClick={resetFileState} variant="ghost" size="sm" className="text-red-400">
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => handleFileSelect(e, 'expense')}
                        className="hidden"
                      />
                      <input
                        ref={cameraInputRef}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        onChange={(e) => handleFileSelect(e, 'expense')}
                        className="hidden"
                      />
                      <Button 
                        onClick={() => fileInputRef.current?.click()} 
                        variant="outline" 
                        className="flex-1 border-amber-500/50 text-amber-300"
                      >
                        <Upload className="w-4 h-4 mr-2" /> Importer
                      </Button>
                      <Button 
                        onClick={() => cameraInputRef.current?.click()} 
                        variant="outline" 
                        className="flex-1 border-amber-500/50 text-amber-300"
                      >
                        <Camera className="w-4 h-4 mr-2" /> Photo
                      </Button>
                    </div>
                  )}
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="text-sm text-white/70 mb-1 block">Description *</label>
                    <Input
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      placeholder="Ex: Essence, Repas client..."
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-white/70 mb-1 block">Montant (€) *</label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.amount}
                        onChange={(e) => setFormData({...formData, amount: e.target.value})}
                        placeholder="0.00"
                        className="bg-white/10 border-white/20 text-white text-lg font-semibold"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-white/70 mb-1 block">Date *</label>
                      <Input
                        type="date"
                        value={formData.date}
                        onChange={(e) => setFormData({...formData, date: e.target.value})}
                        className="bg-white/10 border-white/20 text-white"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-sm text-white/70 mb-1 block">Catégorie</label>
                    <div className="grid grid-cols-4 gap-2">
                      {expenseCategories.map(cat => {
                        const IconComp = cat.icon;
                        return (
                          <button
                            key={cat.id}
                            onClick={() => setFormData({...formData, category: cat.id})}
                            className={`p-2 rounded-lg border transition flex flex-col items-center gap-1 ${
                              formData.category === cat.id 
                                ? 'border-white/50 bg-white/10' 
                                : 'border-white/10 hover:border-white/30'
                            }`}
                          >
                            <IconComp className="w-5 h-5" style={{ color: cat.color }} />
                            <span className="text-xs text-white/70 text-center leading-tight">{cat.name}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <label className="text-sm text-white/70 mb-1 block">Notes</label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({...formData, notes: e.target.value})}
                      placeholder="Notes optionnelles..."
                      rows={2}
                      className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white resize-none"
                    />
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <Button onClick={() => { setShowExpenseModal(false); resetFileState(); }} variant="outline" className="flex-1 border-white/20 text-white">
                    Annuler
                  </Button>
                  <Button onClick={saveExpense} className="flex-1 bg-red-500 hover:bg-red-600">
                    <Save className="w-4 h-4 mr-2" />
                    {editingItem ? 'Modifier' : 'Enregistrer'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal Visualisation Fichier */}
        {showFileModal && viewingFile && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowFileModal(false)}>
            <div className="bg-gray-800 rounded-xl border border-gray-700 max-w-4xl w-full max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
              <div className="p-4 border-b border-gray-700 flex items-center justify-between">
                <h3 className="text-white font-medium flex items-center gap-2">
                  <FileText className="w-5 h-5 text-emerald-400" />
                  {viewingFile.name || 'Justificatif'}
                </h3>
                <Button onClick={() => setShowFileModal(false)} variant="ghost" size="sm" className="text-gray-400">
                  <X className="w-5 h-5" />
                </Button>
              </div>
              <div className="p-4 flex items-center justify-center bg-gray-900 min-h-[400px] max-h-[70vh] overflow-auto">
                {viewingFile.type?.startsWith('image/') ? (
                  <img 
                    src={`data:${viewingFile.type};base64,${viewingFile.data}`} 
                    alt="Justificatif" 
                    className="max-w-full max-h-[65vh] object-contain"
                  />
                ) : viewingFile.type === 'application/pdf' ? (
                  <iframe 
                    src={`data:application/pdf;base64,${viewingFile.data}`}
                    className="w-full h-[65vh]"
                    title="Justificatif PDF"
                  />
                ) : (
                  <div className="text-center text-white/70">
                    <FileText className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p>Aperçu non disponible</p>
                    <Button 
                      onClick={() => {
                        const link = document.createElement('a');
                        link.href = `data:${viewingFile.type};base64,${viewingFile.data}`;
                        link.download = viewingFile.name || 'justificatif';
                        link.click();
                      }}
                      className="mt-4 bg-emerald-500"
                    >
                      Télécharger
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Modal Import depuis Comptabilité */}
        {showImportModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-900 rounded-xl w-full max-w-2xl max-h-[80vh] flex flex-col border border-white/10">
              {/* Header */}
              <div className="p-4 border-b border-white/10 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <FileDown className="w-5 h-5 text-blue-400" />
                  Importer depuis Comptabilité
                </h3>
                <Button onClick={() => setShowImportModal(false)} variant="ghost" size="sm" className="text-gray-400">
                  <X className="w-4 h-4" />
                </Button>
              </div>

              {/* Content */}
              <div className="p-4 flex-1 overflow-y-auto">
                {loadingDocs ? (
                  <div className="text-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-400" />
                    <p className="text-white/60 mt-2">Chargement des documents...</p>
                  </div>
                ) : accountingDocs.length === 0 ? (
                  <div className="text-center py-8">
                    <FileText className="w-12 h-12 mx-auto text-white/30 mb-4" />
                    <p className="text-white/60">Aucun document à importer</p>
                    <p className="text-white/40 text-xs mt-2">
                      (documents déjà importés ou sans justificatif)
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Info aide */}
                    <div className="mb-3 p-2 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-200 text-xs">
                      ℹ️ Saisissez ou corrigez le montant si nécessaire (ex: photos non lues par l'IA). Les documents à 0€ ne peuvent pas être importés.
                    </div>

                    {/* Sélectionner tout */}
                    <div className="mb-4 flex items-center justify-between">
                      <button 
                        onClick={toggleSelectAll}
                        className="flex items-center gap-2 text-blue-400 hover:text-blue-300 text-sm"
                        data-testid="import-toggle-select-all"
                      >
                        {selectedDocs.length === accountingDocs.length ? (
                          <CheckSquare className="w-4 h-4" />
                        ) : (
                          <Square className="w-4 h-4" />
                        )}
                        {selectedDocs.length === accountingDocs.length ? 'Tout désélectionner' : 'Tout sélectionner'}
                      </button>
                      <span className="text-white/60 text-sm">
                        {selectedDocs.length} sélectionné(s) sur {accountingDocs.length}
                      </span>
                    </div>

                    {/* Liste des documents */}
                    <div className="space-y-2">
                      {accountingDocs.map(doc => {
                        const editedVal = editedAmounts[doc.id];
                        const baseAmount = doc.amount_ttc || doc.amount || 0;
                        const displayAmount = editedVal !== undefined && editedVal !== ''
                          ? parseFloat(editedVal) || 0
                          : baseAmount;
                        const isMissingAmount = !displayAmount || displayAmount <= 0;
                        const isSelected = selectedDocs.includes(doc.id);

                        return (
                          <div 
                            key={doc.id}
                            className={`p-3 rounded-lg border transition-all ${
                              isSelected
                                ? (isMissingAmount ? 'border-red-500 bg-red-500/10' : 'border-blue-500 bg-blue-500/10')
                                : 'border-white/10 bg-white/5 hover:border-white/20'
                            }`}
                            data-testid={`import-doc-row-${doc.id}`}
                          >
                            <div className="flex items-start gap-3">
                              <button
                                onClick={() => toggleDocSelection(doc.id)}
                                className={`w-5 h-5 mt-1 rounded border flex items-center justify-center flex-shrink-0 ${
                                  isSelected ? 'bg-blue-500 border-blue-500' : 'border-white/30'
                                }`}
                                data-testid={`import-doc-checkbox-${doc.id}`}
                              >
                                {isSelected && <Check className="w-3 h-3 text-white" />}
                              </button>
                              <div className="flex-1 cursor-pointer" onClick={() => toggleDocSelection(doc.id)}>
                                <div className="flex items-center justify-between gap-2">
                                  <span className="font-medium text-white truncate">
                                    {doc.title || doc.reference || doc.supplier || doc.description || 'Document sans titre'}
                                  </span>
                                  {doc.has_file && (
                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 flex-shrink-0">
                                      📎 Justificatif
                                    </span>
                                  )}
                                </div>
                                <div className="text-xs text-white/50 mt-1 flex items-center gap-2 flex-wrap">
                                  <span>{doc.date?.slice(0, 10) || 'Date inconnue'}</span>
                                  {doc.supplier && (
                                    <>
                                      <span>•</span>
                                      <span>{doc.supplier}</span>
                                    </>
                                  )}
                                </div>
                              </div>
                              {/* Champ montant éditable */}
                              <div className="flex flex-col items-end gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                                <div className="flex items-center gap-1">
                                  <Input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={editedVal !== undefined ? editedVal : (baseAmount || '')}
                                    onChange={(e) => setEditedAmounts(prev => ({ ...prev, [doc.id]: e.target.value }))}
                                    placeholder="0.00"
                                    className={`w-24 h-8 text-right text-sm font-semibold bg-slate-800 ${
                                      isMissingAmount
                                        ? 'border-red-500 text-red-300'
                                        : 'border-white/20 text-blue-300'
                                    }`}
                                    data-testid={`import-doc-amount-${doc.id}`}
                                  />
                                  <span className="text-sm text-white/60">€</span>
                                </div>
                                {isMissingAmount && (
                                  <span className="text-[10px] text-red-400 font-medium">
                                    ⚠️ Montant manquant
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-white/10 flex gap-3">
                <Button 
                  onClick={() => setShowImportModal(false)} 
                  variant="outline" 
                  className="flex-1 border-white/20 text-white"
                >
                  Annuler
                </Button>
                <Button 
                  onClick={importSelectedDocs}
                  disabled={selectedDocs.length === 0}
                  className="flex-1 bg-blue-500 hover:bg-blue-600"
                  data-testid="cashflow-import-confirm-btn"
                >
                  Importer {selectedDocs.length > 0 && `(${selectedDocs.length})`}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
